import type { Tool } from "@/data/tools/tools-registry"
import { getRelatedTools } from "@/data/tools/tools-registry"
import ToolFAQ from "./ToolFAQ"
import RelatedTools from "./RelatedTools"
import ToolCTA from "./ToolCTA"
import { useTranslation } from "@/lib/i18n"

type ToolPageLayoutProps = {
  tool: Tool
  children: React.ReactNode
  /** Educational content sections below the tool */
  content?: React.ReactNode
}

function renderH1(h1Title: string, h1Highlight: string) {
  const idx = h1Title.indexOf(h1Highlight)
  if (idx === -1) {
    return <>{h1Title}</>
  }
  const before = h1Title.slice(0, idx)
  const after = h1Title.slice(idx + h1Highlight.length)
  return (
    <>
      {before}
      <span className="gradient-text">{h1Highlight}</span>
      {after}
    </>
  )
}

export default function ToolPageLayout({ tool, children, content }: ToolPageLayoutProps) {
  const relatedTools = getRelatedTools(tool.slug)
  const { translations: t } = useTranslation()

  return (
    <div className="min-h-screen relative overflow-hidden bg-white dark:bg-black transition-colors duration-300">
      {/* Radial fade background */}
      <div className="absolute inset-0 bg-white/50 dark:bg-black/20 [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)] pointer-events-none" />

      {/* Dotted background */}
      <div className="absolute inset-0 bg-[radial-gradient(circle,#80808015_1px,transparent_1px)] bg-[size:20px_20px] pointer-events-none" />

      <div className="relative z-10 pt-28 sm:pt-36 pb-12">
        {/* Hero + Tool */}
        <main className="max-w-5xl mx-auto px-4 sm:px-6">
          {/* Page title */}
          <div className="text-center mb-8 sm:mb-10">
            {/* Badge */}
            <div className="animate-hero-badge mb-4">
              <span className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs sm:text-sm font-semibold tracking-wide uppercase bg-neutral-100 dark:bg-neutral-800/80 text-neutral-600 dark:text-neutral-300 border border-neutral-200/80 dark:border-neutral-700/60">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-brand-accent" />
                {t.toolsPage.free} Instagram Tool
              </span>
            </div>

            {/* H1 — keyword-rich, with highlighted phrase */}
            <h1 className="animate-hero-title text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-neutral-900 dark:text-white tracking-tight mb-4 leading-tight">
              {renderH1(tool.h1Title, tool.h1Highlight)}
            </h1>

            {/* Subtitle */}
            <p className="animate-hero-subtitle text-sm sm:text-base md:text-lg text-neutral-500 dark:text-neutral-400 max-w-2xl mx-auto font-medium leading-relaxed">
              {tool.description}
            </p>
          </div>

          {/* Tool interface */}
          <section aria-label={tool.name} className="animate-hero-tool">
            {children}
          </section>

          {/* Educational content */}
          {content && (
            <div className="mt-16 sm:mt-20">
              {content}
            </div>
          )}
        </main>

        {/* Related Tools */}
        {relatedTools.length > 0 && (
          <RelatedTools tools={relatedTools} />
        )}

        {/* CTA */}
        <ToolCTA headline={tool.cta.headline} description={tool.cta.description} />

        {/* FAQ */}
        {tool.faq.length > 0 && (
          <ToolFAQ faqs={tool.faq} toolName={tool.shortName} />
        )}
      </div>
    </div>
  )
}
